import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@core/components/ui/badge";
import { Button } from "@core/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@core/components/ui/card";
import AccountPageTemplate from "./AccountPageTemplate";
import {
  billingService,
  SubscriptionViewModelMapper,
} from "@core/services/illing";
import type { Subscription } from "@core/services/illing/types";
import { useAuth, type Profile } from "@core/contexts/AuthContext";
import {
  BILLING_PLAN_NAMES,
  BILLING_LABELS,
  BILLING_STATUS,
  BILLING_STATUS_CONFIG,
  BILLING_TEST_IDS,
  BILLING_MESSAGES,
} from "@core/constants/billing";

interface SubscriptionState {
  planName: string;
  amountLabel: string;
  periodLabel: string;
  nextBillingDate: string;
  renewalDate: string;
  status: keyof typeof BILLING_STATUS;
}

const initialSubscription: SubscriptionState = {
  planName: "DooDates Pro",
  amountLabel: "19,00 €",
  periodLabel: "mensuel",
  nextBillingDate: "15 avril 2026",
  renewalDate: "15 avril 2026",
  status: BILLING_STATUS.ACTIVE,
};

// Utiliser BILLING_STATUS_CONFIG des constantes

export default function AccountSubscription() {
  const { user, profile } = useAuth();
  const { t } = useTranslation("common");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [waiverError, setWaiverError] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await billingService.getCurrentSubscription();
      setSubscription(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Dérive l'abonnement directement du profil en mémoire (pas d'appel réseau).
  // Évite que la page reste bloquée sur "Chargement…" quand un fetch réseau tarde.
  useEffect(() => {
    if (!user || !profile) return;
    const rawPlanType = (profile.plan_type || "free").toLowerCase();
    const PAID_PLANS: Record<
      string,
      { id: string; nameKey: string; amount: number }
    > = {
      solo: {
        id: "SOLO",
        nameKey: "account.subscription.plan_name_solo",
        amount: 3000,
      },
      dooquest_pro: {
        id: "DOOQUEST_PRO",
        nameKey: "account.subscription.plan_name_dooquest_pro",
        amount: 7000,
      },
      // Legacy alias : lignes pas encore migrées de dooform_pro → dooquest_pro.
      dooform_pro: {
        id: "DOOQUEST_PRO",
        nameKey: "account.subscription.plan_name_dooquest_pro",
        amount: 7000,
      },
    };
    const paid = PAID_PLANS[rawPlanType];
    const plan = paid
      ? {
          id: paid.id,
          name: t(paid.nameKey),
          amount: paid.amount,
          currency: "EUR",
          period: "yearly" as const,
          features: [],
        }
      : {
          id: "FREE",
          name: t("account.subscription.plan_name_free"),
          amount: 0,
          currency: "EUR",
          period: "lifetime" as const,
          features: [],
        };
    const p = profile as Profile & {
      lemonsqueezy_subscription_id?: string;
      lemonsqueezy_status?: string;
      lemonsqueezy_current_period_start?: string;
      lemonsqueezy_current_period_end?: string;
      lemonsqueezy_cancel_at_period_end?: boolean;
    };
    setSubscription({
      id: p.lemonsqueezy_subscription_id || "default",
      plan,
      status: (p.lemonsqueezy_status as Subscription["status"]) || "active",
      currentPeriodStart: p.lemonsqueezy_current_period_start
        ? new Date(p.lemonsqueezy_current_period_start)
        : new Date(),
      currentPeriodEnd: p.lemonsqueezy_current_period_end
        ? new Date(p.lemonsqueezy_current_period_end)
        : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
      cancelAtPeriodEnd: p.lemonsqueezy_cancel_at_period_end || false,
    });
    setIsLoading(false);
  }, [user, profile]);

  // Mapper vers le ViewModel
  const viewModel = subscription
    ? SubscriptionViewModelMapper.toViewModel(subscription)
    : null;

  const handleCancelRenewal = async () => {
    if (!subscription) return;

    try {
      await billingService.cancelRenewal();
      await refetch(); // Rafraîchir les données
    } catch (error) {
      console.error("Failed to cancel renewal:", error);
    }
  };

  const handleConfirmCancelRenewal = async () => {
    await handleCancelRenewal();
    setShowCancelConfirm(false);
  };

  const handleResumeRenewal = async () => {
    if (!subscription) return;

    try {
      await billingService.resumeRenewal();
      await refetch(); // Rafraîchir les données
    } catch (error) {
      console.error("Failed to resume renewal:", error);
    }
  };

  const handleUpgradeToSolo = () => {
    if (!waiverAccepted) {
      setWaiverError(true);
      document
        .getElementById("withdrawal-waiver")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setWaiverError(false);
    const consentTimestamp = new Date().toISOString();
    const checkoutUrl = new URL(
      "https://doodates.lemonsqueezy.com/checkout/buy/b8d2dcb2-4a21-4a0e-a2ed-e042175584c6",
    );
    checkoutUrl.searchParams.set("checkout[custom][waiver_accepted]", "true");
    checkoutUrl.searchParams.set(
      "checkout[custom][waiver_accepted_at]",
      consentTimestamp,
    );
    window.location.href = checkoutUrl.toString();
  };

  if (isLoading) {
    return (
      <AccountPageTemplate
        title={t("account.subscription.title")}
        description={BILLING_MESSAGES.LOADING}
        testId={BILLING_TEST_IDS.SUBSCRIPTION_PAGE}
      >
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-gray-800 rounded-lg"></div>
        </div>
      </AccountPageTemplate>
    );
  }

  if (error || !subscription || !viewModel) {
    return (
      <AccountPageTemplate
        title={BILLING_MESSAGES.ERROR_TITLE}
        description={BILLING_MESSAGES.ERROR_DESCRIPTION}
        testId={BILLING_TEST_IDS.SUBSCRIPTION_PAGE}
      >
        <Card className="border-red-500/40 bg-red-500/10">
          <CardContent className="p-6">
            <p className="text-red-200">{BILLING_MESSAGES.ERROR_MESSAGE}</p>
            <Button
              onClick={() => refetch()}
              variant="outline"
              className="mt-4"
            >
              {BILLING_MESSAGES.RETRY}
            </Button>
          </CardContent>
        </Card>
      </AccountPageTemplate>
    );
  }

  return (
    <AccountPageTemplate
      title={t("account.subscription.title")}
      description={t("account.subscription.description")}
      testId={BILLING_TEST_IDS.SUBSCRIPTION_PAGE}
    >
      <div className="space-y-4">
        <Card
          className="border-gray-800 bg-gray-900/80"
          data-testid={BILLING_TEST_IDS.SUBSCRIPTION_SUMMARY_CARD}
        >
          <CardHeader className="flex flex-row items-start justify-between space-y-0">
            <div>
              <CardTitle className="text-white">
                {t("account.subscription.current_plan", {
                  plan: viewModel.planName,
                })}
              </CardTitle>
              <p className="mt-1 text-sm text-gray-300">
                {viewModel.amountLabel} / {viewModel.periodLabel}
              </p>
            </div>
            <Badge
              className={viewModel.statusClassName}
              data-testid={BILLING_TEST_IDS.SUBSCRIPTION_STATUS_BADGE}
            >
              {viewModel.statusLabel}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-gray-300">
            {!viewModel.isLifetime && (
              <p>
                <span className="text-gray-400">
                  {t("account.subscription.renewal_date_label")}
                </span>{" "}
                {viewModel.renewalDate}
              </p>
            )}
            {viewModel.trialEndDate && (
              <p>
                <span className="text-gray-400">
                  {t("account.subscription.trial_end_label")}
                </span>{" "}
                {viewModel.trialEndDate}
              </p>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              {viewModel.planName === BILLING_PLAN_NAMES.FREE && (
                <div className="flex w-full flex-col gap-2">
                  <label
                    id="withdrawal-waiver"
                    className={`flex items-start gap-2 cursor-pointer text-[11px] leading-snug rounded-md px-2 py-1.5 border transition-colors ${
                      waiverError
                        ? "border-red-500/70 bg-red-900/20 text-red-300"
                        : "border-white/10 bg-black/20 text-gray-400"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={waiverAccepted}
                      onChange={(e) => {
                        setWaiverAccepted(e.target.checked);
                        if (e.target.checked) setWaiverError(false);
                      }}
                      className="mt-0.5 w-3.5 h-3.5 flex-shrink-0 accent-purple-500 cursor-pointer"
                      aria-invalid={waiverError}
                    />
                    <span>
                      {t("account.subscription.waiver_consent")}{" "}
                      <a
                        href="/docs/cgu"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-400 underline hover:text-purple-300"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t("account.subscription.waiver_details_link")}
                      </a>
                    </span>
                  </label>
                  <Button
                    onClick={handleUpgradeToSolo}
                    disabled={!waiverAccepted}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={
                      BILLING_TEST_IDS.SUBSCRIPTION_UPGRADE_TO_SOLO_BUTTON
                    }
                  >
                    {t(BILLING_LABELS.UPGRADE_TO_SOLO)}
                  </Button>
                </div>
              )}

              {viewModel.canCancelRenewal && (
                <Button
                  variant="destructive"
                  onClick={() => setShowCancelConfirm(true)}
                  data-testid={
                    BILLING_TEST_IDS.SUBSCRIPTION_CANCEL_RENEWAL_BUTTON
                  }
                >
                  {t(BILLING_LABELS.CANCEL_RENEWAL)}
                </Button>
              )}

              {viewModel.canResumeRenewal && (
                <Button
                  variant="outline"
                  onClick={handleResumeRenewal}
                  data-testid={
                    BILLING_TEST_IDS.SUBSCRIPTION_RESUME_RENEWAL_BUTTON
                  }
                >
                  {t(BILLING_LABELS.RESUME_RENEWAL)}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {showCancelConfirm && (
          <Card
            className="border-red-500/40 bg-red-500/10"
            data-testid={BILLING_TEST_IDS.SUBSCRIPTION_CANCEL_CONFIRMATION}
          >
            <CardHeader>
              <CardTitle className="text-red-200">
                {BILLING_MESSAGES.CONFIRM_CANCEL_TITLE}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-red-100">
              <p>
                {t("account.subscription.cancel_notice_before")}{" "}
                <strong>{viewModel.renewalDate}</strong>
                {t("account.subscription.cancel_notice_after")}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="destructive"
                  onClick={handleConfirmCancelRenewal}
                  data-testid={
                    BILLING_TEST_IDS.SUBSCRIPTION_CONFIRM_CANCEL_BUTTON
                  }
                >
                  {t(BILLING_LABELS.CONFIRM_CANCEL)}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowCancelConfirm(false)}
                  data-testid={BILLING_TEST_IDS.SUBSCRIPTION_KEEP_PLAN_BUTTON}
                >
                  {t(BILLING_LABELS.KEEP_PLAN)}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AccountPageTemplate>
  );
}
